import { useState } from 'react';
import {
    Path,
    FieldValues,
    UseFormSetError,
    UseFormClearErrors,
    UseFormGetValues,
} from 'react-hook-form';
import { FetchErrorsType, FetchErrorsTypeSchema } from '@/types/types/general/errors';
import { getErrorMessageText, getFetchErrors } from '@/utils/getFetchErrorMessage';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { RESPONSE_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { isEmpty } from '@/utils/fp/isEmpty';
import { isObject } from '@/types/typeGuards/general';

interface UseServerErrorsProps<T extends FieldValues> {
    setError: UseFormSetError<T>;
    clearErrors: UseFormClearErrors<T>;
    getValues: UseFormGetValues<T>;
    displayAllErrors?: boolean;
}

export const useServerErrors = <T extends FieldValues>({
    setError,
    clearErrors: clearFormFieldErrors,
    getValues: getFormValues,
    displayAllErrors = false,
}: UseServerErrorsProps<T>) => {
    const [generalServerError, setGeneralServerError] = useState<string | FetchErrorsType>(
        undefined,
    );
    const [unassignedPropertyErrors, setUnassignedPropertyErrors] = useState<string[] | undefined>(
        undefined,
    );

    const { t } = usePhrase();

    const propertyErrorExists = (err: FetchErrorsType) =>
        err?.propertyErrors &&
        isObject(err.propertyErrors) &&
        !isEmpty(err.propertyErrors) &&
        Object.values(err.propertyErrors).some((errors) => !isEmpty(errors));

    const doesFormContainFetchErrors = (fetchErrors: FetchErrorsType, formFieldNames: string[]) =>
        fetchErrors &&
        Object.keys(fetchErrors.propertyErrors).some((name) => {
            const rootFieldName = name.split('.')[0];
            return formFieldNames.includes(rootFieldName);
        });

    const updateFormFieldErrors = (err: FetchErrorsType) => {
        if (!err) return;

        const formFieldNames = Object.keys(getFormValues()) ?? [];
        const unknownFieldsErrors: string[] = [];

        let firstErrorSet = false;

        Object.entries(err.propertyErrors).forEach(([inputName, data]) => {
            const validErrors = data.filter(
                (item) => (!!item.isMessageUserFriendly && !!item.message) || !!item.phraseId,
            );
            if (validErrors.length === 0) return;

            const errorMessage = getErrorMessage(validErrors);
            const isNestedPath = inputName.includes('.');
            const rootFieldName = isNestedPath ? inputName.split('.')[0] : inputName;

            if (formFieldNames.includes(rootFieldName)) {
                setError(
                    inputName as Path<T>,
                    {
                        type: 'custom',
                        message: errorMessage,
                    },
                    { shouldFocus: !firstErrorSet },
                );
                firstErrorSet = true;
            } else {
                unknownFieldsErrors.push(`${inputName}: ${errorMessage}`);
            }
        });

        if (unknownFieldsErrors.length > 0) {
            setUnassignedPropertyErrors(unknownFieldsErrors);
        }
    };

    const getErrorMessage = (data: NonNullable<FetchErrorsType>['propertyErrors'][string]) => {
        if (data.length === 0) return '';

        if (displayAllErrors) {
            return data.map((error) => getErrorMessageText(error, t)).join('\n');
        }

        return getErrorMessageText(data[0], t);
    };

    const handleServerErrors = (err: unknown) => {
        const error = FetchErrorsTypeSchema.safeParse(err).success
            ? (err as FetchErrorsType)
            : getFetchErrors(err);

        const hasGeneralError = error?.generalErrors && !isEmpty(error.generalErrors);
        const hasPropertyError = propertyErrorExists(error);

        const resetErrors = () => {
            clearFormFieldErrors();
            setUnassignedPropertyErrors(undefined);
        };

        if (typeof error === 'string') {
            setGeneralServerError(error);
            resetErrors();
            return;
        }

        setGeneralServerError(hasGeneralError ? error : undefined);
        setUnassignedPropertyErrors(undefined);
        clearFormFieldErrors();

        if (hasPropertyError) {
            updateFormFieldErrors(error);
        } else if (!hasGeneralError && typeof error !== 'string') {
            setGeneralServerError(t(RESPONSE_ERROR_PHRASES.UNKNOWN, { asString: true }));
            resetErrors();
        } else {
            resetErrors();
        }
    };

    const clearGeneralErrors = () => {
        setGeneralServerError(undefined);
        setUnassignedPropertyErrors(undefined);
    };

    const clearAllErrors = () => {
        clearFormFieldErrors();
        clearGeneralErrors();
    };

    return {
        serverErrors: {
            generalServerError,
            unassignedPropertyErrors,
        },
        handleServerErrors,
        propertyErrorExists,
        doesFormContainFetchErrors,
        clearAllErrors,
        clearGeneralErrors,
    };
};
